import { foldAllExceptRoot } from '@/components/Code/Code.utils';
import { Message } from '@/components/Message';
import { MessageUnionType, RunInputSchema, RunSchema } from '@/types/schema';
import {
  getMessageContent,
  getMessageFields,
  getMessageType,
  isMessageLike,
  maybeGetArrayOfMessages,
  maybeGetOpenAIResponsesInputMessages,
  setMessageContent,
  setMessageType,
} from '@/utils/messages';

import { CollapsibleAdditionalFields } from './RunDetailsIO';
import {
  PageDocument,
  RunPageDocumentList,
  asPageDocumentList,
} from './RunPageDocument';
import { RunPageMessages } from './RunPageMessages';

export function RunInput({
  inputs,
  attachments,
  foldDistractions,
  onEditInputs,
  runType,
}: {
  inputs: RunInputSchema | undefined;
  attachments: RunSchema['inputs_s3_urls'];
  foldDistractions?: { prevRun: RunSchema | undefined };
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  runType?: string;
}) {
  const variant = foldDistractions ? 'conversation' : undefined;

  const isSingleInputKey = inputs && Object.keys(inputs).length === 1;
  const singleKey = isSingleInputKey ? Object.keys(inputs)[0] : undefined;

  if (
    variant === 'conversation' &&
    inputs &&
    singleKey &&
    typeof inputs[singleKey] === 'string'
  ) {
    return (
      <Message
        role="HUMAN"
        content={inputs[singleKey] ?? ''}
        attachments={attachments}
        onCreateEditor={foldAllExceptRoot}
        variant={variant}
        selectedConversationKey={singleKey}
        onEditContent={
          onEditInputs
            ? (value) => {
                const inputsNew = { ...inputs, [singleKey]: value };
                onEditInputs(inputsNew);
              }
            : undefined
        }
      />
    );
  }

  // is inputs[singleKey] a message list?
  if (isSingleInputKey && singleKey) {
    const maybeMessagesArray = maybeGetArrayOfMessages(inputs[singleKey]);
    if (maybeMessagesArray) {
      return (
        <RunPageMessages
          messages={maybeMessagesArray}
          attachments={attachments}
          foldDistractions={foldDistractions}
          onEdit={
            onEditInputs
              ? (value) => {
                  onEditInputs({ [singleKey]: value });
                }
              : undefined
          }
        />
      );
    }
  }

  // is inputs[singleKey] a message
  if (isSingleInputKey && singleKey && isMessageLike(inputs[singleKey])) {
    const handleEdit = (messageSetter) =>
      onEditInputs
        ? (value) => {
            const inputsNew = { ...inputs };
            inputsNew[singleKey] = messageSetter(inputs[singleKey], value);
            onEditInputs(inputsNew);
          }
        : undefined;
    return (
      <Message
        role={getMessageType(inputs[singleKey]) ?? 'HUMAN'}
        content={getMessageContent(inputs[singleKey]) ?? ''}
        fields={getMessageFields(inputs[singleKey])}
        attachments={attachments}
        onEditContent={handleEdit(setMessageContent)}
        onChangeRole={handleEdit(setMessageType)}
      />
    );
  }

  // is inputs a message list?
  const maybeMessagesArray = maybeGetArrayOfMessages(inputs);
  if (maybeMessagesArray) {
    return (
      <RunPageMessages
        messages={maybeMessagesArray}
        attachments={attachments}
        foldDistractions={foldDistractions}
        onEdit={onEditInputs}
      />
    );
  }

  // is `inputs` a message
  if (isMessageLike(inputs)) {
    const handleEdit = (messageSetter) =>
      onEditInputs
        ? (value) => {
            onEditInputs(messageSetter(inputs, value));
          }
        : undefined;
    return (
      <Message
        role={getMessageType(inputs) ?? 'HUMAN'}
        content={getMessageContent(inputs) ?? ''}
        fields={getMessageFields(inputs)}
        attachments={attachments}
        onEditContent={handleEdit(setMessageContent)}
        onChangeRole={handleEdit(setMessageType)}
      />
    );
  }

  // is OpenAI Responses API inputs
  const openAIResponsesInput = maybeGetOpenAIResponsesInputMessages(inputs);
  if (runType == 'llm' && openAIResponsesInput) {
    const unprocessedKwargs =
      inputs && typeof inputs === 'object'
        ? Object.fromEntries(
            Object.entries(inputs).filter(
              ([key]) => !['input', 'instructions'].includes(key)
            )
          )
        : undefined;
    return (
      <div className="flex max-w-full flex-col gap-4">
        <RunPageMessages
          messages={openAIResponsesInput}
          attachments={attachments}
          foldDistractions={foldDistractions}
          onEdit={
            onEditInputs
              ? (messages: MessageUnionType[]) => {
                  const instructions = messages
                    .filter((msg) => 'role' in msg && msg.role === 'system')
                    .map((msg) => getMessageContent(msg))
                    .join('\n');
                  const input = messages.filter(
                    (msg) => !('role' in msg) || msg.role !== 'system'
                  );

                  onEditInputs({
                    ...inputs,
                    input: input,
                    instructions: instructions,
                  });
                }
              : undefined
          }
        />
        {unprocessedKwargs && Object.keys(unprocessedKwargs).length > 0 && (
          <div className="flex flex-col gap-2 rounded-lg border border-secondary">
            <CollapsibleAdditionalFields
              kwargs={unprocessedKwargs}
              variant={variant}
              attachments={attachments}
              expanded={Boolean(runType !== 'llm' || onEditInputs)}
              onEditKwargs={
                onEditInputs
                  ? (value) => {
                      onEditInputs({ ...inputs, ...value });
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    );
  }

  const documentEntries =
    inputs && typeof inputs === 'object'
      ? Object.entries(inputs)
          .map(([key, value]) => [key, asPageDocumentList(value)])
          .filter((key): key is [string, PageDocument[]] => !!key[1])
      : undefined;
  const originalDocuments = documentEntries?.map(([key]) => [
    key,
    inputs?.[key],
  ]);

  const messagesEntries =
    inputs && typeof inputs === 'object'
      ? Object.entries(inputs)
          .map(([key, value]) => {
            const messages = maybeGetArrayOfMessages(value);
            return messages ? [key, messages] : null;
          })
          .filter(
            (entry): entry is [string, MessageUnionType[]] => entry !== null
          )
      : undefined;

  if (
    inputs &&
    ((documentEntries?.length ?? 0) || (messagesEntries?.length ?? 0))
  ) {
    const unprocessedKwargs = Object.fromEntries(
      Object.entries(inputs).filter(
        ([key]) =>
          !documentEntries?.find(([k]) => k === key) &&
          !messagesEntries?.find(([k]) => k === key)
      )
    );

    return (
      <div className="flex max-w-full flex-col gap-4">
        {Object.keys(unprocessedKwargs ?? {}).length > 0 && (
          <Message
            content=""
            variant={variant}
            kwargs={unprocessedKwargs}
            attachments={attachments}
            testid="run-input-component-kv"
            onEditKwargs={
              onEditInputs
                ? (value) => {
                    // preserve documentEntries and messagesEntries
                    const newInputs = {};
                    originalDocuments?.forEach(([key, docs]) => {
                      newInputs[key] = docs;
                    });
                    messagesEntries?.forEach(([key, messages]) => {
                      newInputs[key] = messages;
                    });
                    // update the edited key
                    onEditInputs({ ...newInputs, ...value });
                  }
                : undefined
            }
          />
        )}

        {documentEntries?.map(([key, docs]) => (
          <RunPageDocumentList key={key} objectKey={key} documents={docs} />
        ))}

        {messagesEntries?.map(([key, messages]) => (
          <RunPageMessages
            key={key}
            objectKey={key}
            messages={messages}
            onEdit={
              onEditInputs
                ? (value) => {
                    onEditInputs({ ...inputs, [key]: value });
                  }
                : undefined
            }
          />
        ))}
      </div>
    );
  }

  return (
    <Message
      content=""
      variant={variant}
      kwargs={inputs}
      attachments={attachments}
      onCreateEditor={foldDistractions ? foldAllExceptRoot : undefined}
      testid="run-input-component-kv"
      onEditKwargs={onEditInputs}
    />
  );
}
